import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Loader2, Gem, Copy, Check, Sparkles, Image as ImageIcon, Upload, RotateCcw, Wand2 } from "lucide-react";
import { sr } from "../../../lib/sr";

const TONE_ACCENT = {
  Editorial: "#1E293B",
  Aspirational: "#9A6A2C",
  Minimalist: "#334155",
};

function PresetCard({ preset, i, onChanged }) {
  const [copied, setCopied] = useState(false);
  const [imgCopied, setImgCopied] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [genPrompt, setGenPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const fileRef = useRef(null);
  const accent = TONE_ACCENT[preset.tone] || "var(--navy)";
  const img = preset.image_url;
  const custom = !!preset.custom_image;
  const copy = async () => {
    const text = `${preset.headline}\n\n${preset.body}\n\n${preset.cta}${img ? `\n\nHero image: ${img}` : ""}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${preset.tone} preset copied — ready for your campaign`);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy — select and copy manually.");
    }
  };
  const copyImg = async () => {
    if (!img) return;
    try {
      await navigator.clipboard.writeText(img);
      setImgCopied(true);
      toast.success("Hero image link copied");
      setTimeout(() => setImgCopied(false), 1600);
    } catch {
      toast.error("Could not copy the image link.");
    }
  };
  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file (PNG, JPG or WebP)."); return; }
    setUploading(true);
    try {
      await sr.luxuryPresetImageUpload(preset.tone, file);
      toast.success(`${preset.tone} hero updated with your image`);
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload the image.");
    } finally { setUploading(false); }
  };
  const revert = async () => {
    setUploading(true);
    try {
      await sr.luxuryPresetImageReset(preset.tone);
      toast.success(`${preset.tone} hero reverted to the default`);
      if (onChanged) await onChanged();
    } catch {
      toast.error("Could not revert the image.");
    } finally { setUploading(false); }
  };
  const generate = async () => {
    const p = genPrompt.trim();
    if (p.length < 3) { toast.error("Describe what to feature (a few words)."); return; }
    setGenerating(true);
    try {
      await sr.luxuryPresetImageGenerate(preset.tone, p);
      toast.success(`${preset.tone} hero generated`);
      setGenOpen(false); setGenPrompt("");
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate the image — try again.");
    } finally { setGenerating(false); }
  };
  return (
    <div data-testid={`luxury-preset-${i}`} style={{ background: "#fff", border: "1px solid var(--border)", borderLeft: `3px solid ${accent}`, borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <input ref={fileRef} type="file" accept="image/*" onChange={onPick} style={{ display: "none" }} data-testid={`luxury-preset-file-${i}`} />
      {img && (
        <div style={{ position: "relative", lineHeight: 0 }}>
          <a href={img} target="_blank" rel="noreferrer" data-testid={`luxury-preset-img-${i}`} title="Open full hero image" style={{ display: "block" }}>
            <img src={img} alt={`${preset.tone} prestige hero`} loading="lazy" style={{ width: "100%", height: 118, objectFit: "cover", display: "block" }} />
          </a>
          <span className="sr-badge" style={{ position: "absolute", top: 7, left: 7, background: "rgba(16,24,40,.72)", color: "#fff", fontSize: 10, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase" }}>{preset.tone}</span>
          {custom && <span style={{ position: "absolute", top: 7, right: 7, background: "rgba(16,24,40,.72)", color: "#fff", fontSize: 9.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", borderRadius: 999, padding: "3px 7px" }} data-testid={`luxury-preset-custom-${i}`}>Your image</span>}
          <button onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading} data-testid={`luxury-preset-replace-${i}`} title="Replace with your own product shot" style={{ position: "absolute", bottom: 7, right: 7, border: "none", background: "rgba(16,24,40,.80)", color: "#fff", borderRadius: 999, padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 5 }}>
            {uploading ? <Loader2 size={11} className="sr-spin" /> : <Upload size={11} />} Replace
          </button>
        </div>
      )}
      <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 6 }}>
        {!img && <span className="sr-badge" style={{ background: `${accent}14`, color: accent, fontSize: 10.5, fontWeight: 800, letterSpacing: 0.4, textTransform: "uppercase", alignSelf: "flex-start" }} data-testid={`luxury-preset-tone-${i}`}>{preset.tone}</span>}
        <strong style={{ color: "var(--navy)", fontSize: 15, lineHeight: 1.25 }}>{preset.headline}</strong>
        <p style={{ fontSize: 12.5, color: "var(--muted)", lineHeight: 1.45, margin: 0 }}>{preset.body}</p>
        <span style={{ fontSize: 12, fontWeight: 700, color: accent }}>{preset.cta} →</span>
        <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
          <button onClick={copy} data-testid={`luxury-preset-copy-${i}`} title="Copy headline, body and CTA" className="sr-btn sr-btn-sm" style={{ background: "#fff", padding: "4px 9px", fontSize: 11.5 }}>
            {copied ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy text</>}
          </button>
          {img && (
            <button onClick={copyImg} data-testid={`luxury-preset-copy-img-${i}`} title="Copy the hero image link to drop into a campaign" className="sr-btn sr-btn-sm" style={{ background: "#fff", padding: "4px 9px", fontSize: 11.5 }}>
              {imgCopied ? <><Check size={12} /> Link copied</> : <><ImageIcon size={12} /> Copy image</>}
            </button>
          )}
          {custom && (
            <button onClick={revert} disabled={uploading} data-testid={`luxury-preset-revert-${i}`} title="Revert to the default editorial visual" className="sr-btn sr-btn-sm" style={{ background: "#fff", padding: "4px 9px", fontSize: 11.5 }}>
              <RotateCcw size={12} /> Revert
            </button>
          )}
          <button onClick={() => setGenOpen((o) => !o)} data-testid={`luxury-preset-ai-${i}`} title="Generate a fresh on-brand hero from a prompt" className="sr-btn sr-btn-sm" style={{ background: "#fff", padding: "4px 9px", fontSize: 11.5 }}>
            <Wand2 size={12} /> AI hero
          </button>
        </div>
        {genOpen && (
          <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }} data-testid={`luxury-preset-gen-${i}`}>
            <input className="sr-input" value={genPrompt} onChange={(e) => setGenPrompt(e.target.value)} placeholder="e.g. a gold Swiss watch on black marble" data-testid={`luxury-preset-gen-input-${i}`} style={{ fontSize: 12.5 }} onKeyDown={(e) => e.key === "Enter" && !generating && generate()} />
            <button onClick={generate} disabled={generating} data-testid={`luxury-preset-gen-go-${i}`} className="sr-btn sr-btn-gold sr-btn-sm" style={{ fontSize: 11.5 }}>
              {generating ? <><Loader2 size={12} className="sr-spin" /> Generating…</> : <><Sparkles size={12} /> Generate hero</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Luxury Playbook — a prestige brand-building starting point for luxury clients:
 * one-tap prestige channel mix (brand-building heavy, light search) + copyable
 * prestige creative presets (editorial / aspirational / minimalist tones).
 * Reuses the existing playbook-apply flow to create a pre-loaded draft plan.
 */
export default function LuxuryPlaybook({ onApplied }) {
  const [pb, setPb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = () => sr.luxuryPlaybook()
    .then((r) => setPb((r.playbooks || []).find((p) => p.key === "luxury") || null))
    .catch(() => setPb(null))
    .finally(() => setLoading(false));

  useEffect(() => { reload(); }, []);

  const apply = async () => {
    setBusy(true);
    try {
      await sr.applyPlaybook("luxury");
      toast.success("Prestige channel mix applied — a draft plan was created");
      onApplied && onApplied();
    } catch {
      toast.error("Could not apply the prestige mix.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="sr-muted" style={{ fontSize: 13, marginTop: 14 }}><Loader2 size={13} className="sr-spin" /> Loading the Luxury Playbook…</div>;
  if (!pb) return null;

  const mix = pb.channel_mix || [];
  const presets = pb.creative_presets || [];
  const maxPct = Math.max(1, ...mix.map((c) => c.budget_pct || 0));

  return (
    <div className="sr-card" style={{ marginTop: 14, border: "1px solid var(--gold, #D4A94A)", background: "linear-gradient(180deg,#FBF7EC,#FFFFFF)" }} data-testid="luxury-playbook-card">
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 240 }}>
          <span className="sr-badge sr-badge-gold" data-testid="luxury-playbook-label"><Gem size={11} /> Luxury Playbook</span>
          <h3 style={{ fontSize: 18, marginTop: 8, color: "var(--navy)" }}>{pb.name}</h3>
          <p className="sr-muted" style={{ fontSize: 12.5, marginTop: 6 }}>{pb.offer}</p>
        </div>
        <button className="sr-btn sr-btn-gold" onClick={apply} disabled={busy} data-testid="luxury-playbook-apply">
          {busy ? <Loader2 size={14} className="sr-spin" /> : <><Sparkles size={14} /> Apply prestige channel mix</>}
        </button>
      </div>

      {/* Prestige channel mix */}
      <div style={{ marginTop: 14 }} data-testid="luxury-channel-mix">
        <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Prestige channel mix — brand-building heavy, light search</div>
        <div style={{ display: "grid", gap: 7 }}>
          {mix.map((c) => (
            <div key={c.channel} data-testid={`luxury-ch-${c.budget_pct}`} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 128, fontSize: 12.5, color: "var(--navy)", fontWeight: 700 }}>{c.channel}</span>
              <div style={{ flex: 1, height: 10, background: "#EDE4CF", borderRadius: 999, overflow: "hidden" }}>
                <div style={{ width: `${((c.budget_pct || 0) / maxPct) * 100}%`, height: "100%", background: "var(--gold, #C79A3B)", borderRadius: 999 }} />
              </div>
              <span style={{ width: 38, textAlign: "right", fontSize: 12.5, fontWeight: 700, color: "var(--muted)" }}>{c.budget_pct}%</span>
            </div>
          ))}
        </div>
        <p className="sr-muted" style={{ fontSize: 11.5, marginTop: 8, fontStyle: "italic" }}>Applies this mix to a new draft Growth Plan — edit anything after.</p>
      </div>

      {/* Copyable prestige creative presets */}
      {presets.length > 0 && (
        <div style={{ marginTop: 16 }} data-testid="luxury-presets">
          <div className="sr-eyebrow" style={{ marginBottom: 8 }}>Prestige creative presets — on-brand hero visual + copy, ready to drop into a campaign</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: 10 }}>
            {presets.map((pr, i) => <PresetCard key={pr.tone} preset={pr} i={i} onChanged={reload} />)}
          </div>
        </div>
      )}
    </div>
  );
}
